import { Link, Navigate } from "react-router-dom";
import {
  ArrowRight, Check, Boxes, ClipboardList, CalendarCheck, Wrench, Activity, Microscope,
  PackageSearch, FileBarChart, FileX2, CalendarX2, MessageSquareX,
} from "lucide-react";
import Footer from "@/components/Footer";
import BackToTop from "@/components/BackToTop";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/lib/useMeta";
import { SECTORS } from "@/lib/omSectors";
import {
  DashboardMockup, PhoneMockup, ComparisonTable, OmLandingNav, Breadcrumb,
} from "@/components/marketing/OmLandingParts";

const GOLD = "#C9A84C";
const NAVY = "#0A1628";
const CARD = "#112240";
const STRIP = "#0D1B2A";
const MUTED = "#8892B0";
const RED = "#EF4444";

const ANCHOR_LINKS = [
  ["Asset Register", "asset-register"], ["PM", "preventive"], ["CM", "corrective"],
  ["Reliability", "reliability"], ["RCA", "rca"], ["Reports", "reports"],
];

const PROBLEMS = [
  [FileX2, "No Centralised Asset Register", "Equipment data is scattered across spreadsheets, emails, and paper files. Nobody knows the warranty status or last service date of a critical asset."],
  [CalendarX2, "Missed Preventive Maintenance", "PM schedules exist in someone's head or a shared Excel sheet. Reminders are missed. Compliance drops. Equipment fails early."],
  [MessageSquareX, "Slow Fault Response", "Faults are reported via WhatsApp group messages. There is no ticket, no SLA, no accountability, and no record for the regulator."],
];

const FEATURES = [
  [Boxes, "Asset Register", "Full equipment hierarchy with health scores, warranty tracking, and document storage. Bulk import from Excel.", "/om/assets", "asset-register"],
  [ClipboardList, "Daily Monitoring Log", "Shift-wise parameter readings with expected range validation. Any out-of-range reading triggers an instant WhatsApp and email alert.", "/om/daily-log", "monitoring"],
  [CalendarCheck, "Preventive Maintenance", "Auto-scheduled PM work orders, step-by-step checklists, digital sign-off, and a monthly compliance calendar.", "/om/preventive-maintenance", "preventive"],
  [Wrench, "Corrective Maintenance", "Structured fault ticketing with SLA timers, root cause logging, spare parts consumption, and a complete CM history per asset.", "/om/corrective-maintenance", "corrective"],
  [Activity, "Reliability Analytics", "Dynamic MTBF and MTTR calculation engine with rolling 30, 60, and 90-day windows grouped by asset category. Trend indicators: Improving, Stable, or Degrading.", "/om/reliability", "reliability"],
  [Microscope, "Automated RCA Engine", "Critical fault triggers an automated 5-Why Root Cause Analysis populated from a pre-built fault taxonomy. Reviewed and approved by the O&M Manager. Exported as a branded PDF.", "/om/rca", "rca"],
  [PackageSearch, "Spare Parts Inventory", "Track stock levels, auto-deduct on work order completion, and receive low-stock WhatsApp alerts before a part runs out.", "/om/inventory", "inventory"],
  [FileBarChart, "O&M Reports", "Monthly O&M Summary, Equipment History, SLA Performance, and Regulatory Compliance reports — all generated as branded PDF and Excel in one click.", "/om/reports", "reports"],
];

const STEPS = [
  ["Register Your Assets", "Import your equipment list from Excel or add assets one by one. Set up PM schedules, assign teams, and generate QR codes. Takes less than 2 hours for a 100-asset site."],
  ["Go Live on Site", "Technicians scan QR codes or open the mobile log page to start recording daily readings and raising fault tickets. WhatsApp alerts are active from Day 1."],
  ["Track, Analyse, and Report", "Monitor asset health, reliability trends, and PM compliance from the O&M dashboard. Generate branded compliance reports for any period in one click."],
];

const VOICES = [
  ["PM compliance on our solar plant went from 61% to 93% in the first two months. The automated WhatsApp reminders alone changed how our team works.", "O&M Manager, 50MW Solar Plant, Rajasthan"],
  ["The RCA engine auto-populated a 5-Why analysis on a transformer fault in seconds. We used to spend half a day on this manually. The PDF report went to the client the same day.", "Maintenance Head, Thermal Power Station, Madhya Pradesh"],
  ["Our NHAI O&M quarterly report used to take 3 days of manual data collection. Plan-Sarthi generates it in 20 minutes with full compliance records already attached.", "O&M Concessionaire, 4-Lane Highway Package, Maharashtra"],
];

const PLANS = [
  { name: "Professional Plan", gold: false, blurb: "For independent O&M contractors and single-site operators.",
    items: ["All 8 O&M modules", "Unlimited assets", "PDF and Excel report downloads", "QR code generation", "WhatsApp and email alerts", "Up to 5 team members"],
    cta: ["Start Free Trial", "/signup"] },
  { name: "Enterprise Plan", gold: true, blurb: "For multi-site O&M operations and EPC companies managing client assets.",
    items: ["Everything in Professional", "Multi-site and multi-company workspace", "Custom company branding on all reports", "Dedicated account manager", "Custom RCA template library", "Priority WhatsApp support"],
    cta: ["Talk to Sales", "/contact"] },
];

const Eyebrow = ({ children }) => (
  <p className="text-[16px] font-semibold uppercase" style={{ color: GOLD, letterSpacing: "0.1em" }}>{children}</p>
);

const H2 = ({ children, testid }) => (
  <h2 className="font-display text-2xl sm:text-3xl font-bold text-white" data-testid={testid}>{children}</h2>
);

const Sub = ({ children, center }) => (
  <p className={`text-[14px] mt-3 leading-relaxed max-w-[760px] ${center ? "mx-auto text-center" : ""}`} style={{ color: MUTED }}>{children}</p>
);

const Trust = ({ children }) => (
  <span className="inline-flex items-center gap-1.5 text-[12.5px]" style={{ color: MUTED }}>
    <Check className="w-3.5 h-3.5 shrink-0" style={{ color: GOLD }} /> {children}
  </span>
);

export default function OmLanding() {
  const { user } = useAuth();

  useMeta({
    title: "O&M Module — Operations and Maintenance Platform for EPC | Plan-Sarthi",
    description:
      "India's first AI-powered O&M platform for EPC projects. Asset register, preventive maintenance, fault ticketing, MTBF/MTTR analytics, automated RCA, and compliance reports. Built for Solar, Wind, Thermal, Nuclear, and all Indian infrastructure sectors.",
    path: "/om",
  });

  if (user) return <Navigate to="/om/dashboard" replace />;

  return (
    <div className="min-h-screen text-white" style={{ background: NAVY, fontFamily: "Inter, system-ui, sans-serif" }} data-testid="om-landing-page">
      <OmLandingNav links={ANCHOR_LINKS} />
      <Breadcrumb />

      {/* 1 — Hero */}
      <section className="relative overflow-hidden" data-testid="om-landing-hero">
        <div className="absolute inset-0 pointer-events-none"
             style={{ background: "radial-gradient(60% 70% at 22% 45%, rgba(201,168,76,0.16) 0%, rgba(201,168,76,0.05) 42%, transparent 72%)" }} />
        <div className="relative max-w-[1280px] mx-auto px-5 sm:px-8 py-16 lg:py-24 grid lg:grid-cols-[1.02fr_1fr] gap-12 items-center">
          <div>
            <span className="inline-block border px-2.5 py-1 text-[11px] font-semibold uppercase"
                  style={{ borderColor: "rgba(201,168,76,0.5)", color: GOLD, letterSpacing: "0.12em" }}
                  data-testid="om-landing-label-pill">
              INDIA'S FIRST AI-POWERED O&amp;M PLATFORM
            </span>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-[52px] mt-5" style={{ lineHeight: 1.1 }} data-testid="om-landing-h1">
              Operations and Maintenance, <span style={{ color: GOLD }}>Reimagined for Indian Infrastructure.</span>
            </h1>
            <p className="text-white mt-6 max-w-[540px]" style={{ fontSize: 20, lineHeight: 1.6 }} data-testid="om-landing-subheadline">
              From daily shift logs to automated Root Cause Analysis — Plan-Sarthi's O&amp;M module gives your team
              one platform to monitor assets, manage maintenance, and stay audit-ready. Built for Solar, Wind,
              Thermal, Nuclear, HVAC, Roads, and every major Indian infrastructure sector.
            </p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-9">
              <Link to="/signup" className="inline-flex items-center justify-center h-12 px-7 rounded-lg text-[15px] font-bold transition-transform duration-200 hover:-translate-y-0.5"
                    style={{ background: GOLD, color: NAVY }} data-testid="om-landing-hero-trial-btn">
                Start Free Trial <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <Link to="/contact" className="inline-flex items-center justify-center h-12 px-7 rounded-lg border text-[15px] font-semibold transition-colors duration-200 hover:bg-[#C9A84C]/10"
                    style={{ borderColor: GOLD, color: GOLD }} data-testid="om-landing-hero-demo-btn">
                Book a Demo
              </Link>
              <Link to="/om/sectors" className="inline-flex items-center gap-1.5 text-[14px] font-semibold sm:ml-2 hover:underline"
                    style={{ color: GOLD }} data-testid="om-landing-hero-sectors-link">
                Explore Sectors <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-7">
              <Trust>14-day free trial, no credit card</Trust>
              <Trust>All Indian infrastructure sectors covered</Trust>
              <Trust>WhatsApp alerts included.</Trust>
            </div>
          </div>
          <div className="hidden lg:block"><DashboardMockup /></div>
        </div>
      </section>

      {/* 2 — The challenge */}
      <section className="border-y" style={{ background: STRIP, borderColor: "rgba(255,255,255,0.08)" }} data-testid="om-landing-problem">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 py-16 lg:py-20">
          <Eyebrow>The Challenge</Eyebrow>
          <H2 testid="om-landing-problem-heading">Most O&amp;M Teams Are Still Running on Excel and WhatsApp.</H2>
          <div className="grid md:grid-cols-3 gap-4 mt-10">
            {PROBLEMS.map(([Icon, title, text], i) => (
              <div key={title} className="rounded-lg border p-6" style={{ background: CARD, borderColor: "rgba(255,255,255,0.1)" }}
                   data-testid={`om-landing-problem-${i}`}>
                <Icon className="w-6 h-6" style={{ color: RED, opacity: 0.85 }} />
                <h3 className="font-semibold text-[16px] text-white mt-4">{title}</h3>
                <p className="text-[13px] mt-2.5 leading-relaxed" style={{ color: MUTED }}>{text}</p>
              </div>
            ))}
          </div>
          <p className="text-[14.5px] font-medium text-center mt-9" style={{ color: GOLD }}>
            Plan-Sarthi replaces all of this with one structured, mobile-ready O&amp;M platform.
          </p>
        </div>
      </section>

      {/* 3 — Feature overview */}
      <section id="features" className="max-w-[1280px] mx-auto px-5 sm:px-8 py-16 lg:py-20 scroll-mt-16" data-testid="om-landing-features">
        <H2 testid="om-landing-features-heading">Everything Your O&amp;M Team Needs in One Platform</H2>
        <Sub>
          Eight integrated modules covering the full O&amp;M lifecycle — from asset registration on Day 1 to
          compliance reporting years later.
        </Sub>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
          {FEATURES.map(([Icon, title, text, to, anchor], i) => (
            <div key={title} id={anchor} className="rounded-lg border p-5 flex flex-col scroll-mt-24 transition-transform duration-200 hover:-translate-y-1"
                 style={{ background: CARD, borderColor: "rgba(255,255,255,0.1)" }} data-testid={`om-landing-feature-${i}`}>
              <Icon className="w-6 h-6" style={{ color: GOLD }} />
              <h3 className="font-semibold text-[16px] text-white mt-4">{title}</h3>
              <p className="text-[13px] mt-2.5 leading-relaxed flex-1" style={{ color: MUTED }}>{text}</p>
              <Link to={to} className="inline-flex items-center gap-1.5 text-[13px] font-semibold mt-4 hover:underline"
                    style={{ color: GOLD }} data-testid={`om-landing-feature-link-${i}`}>
                Learn More <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* 4 — QR field feature */}
      <section className="border-y" style={{ background: STRIP, borderColor: "rgba(255,255,255,0.08)" }} data-testid="om-landing-qr">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 py-16 lg:py-20 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
          <div>
            <span className="inline-block border px-2.5 py-1 text-[11px] font-semibold uppercase"
                  style={{ borderColor: "rgba(201,168,76,0.5)", color: GOLD, letterSpacing: "0.12em" }}>
              Field-Ready
            </span>
            <H2 testid="om-landing-qr-heading">Scan a QR Code. Log a Reading. Raise a Fault. Done.</H2>
            <p className="text-[15px] mt-4" style={{ color: MUTED, lineHeight: 1.7 }}>
              Every asset in Plan-Sarthi gets a unique QR code — print it, laminate it, and fix it to the
              equipment. When a technician scans it from their phone, they land instantly on that asset's mobile
              log page. No app to install. No searching for the asset. No paperwork. Log the daily shift reading
              or raise a fault ticket in under 30 seconds — from anywhere on site.
            </p>
            <div className="space-y-2.5 mt-6">
              {["Auto-generates for every registered asset",
                "Mobile page loads in under 3 seconds on 4G",
                "Critical readings trigger instant WhatsApp alert to O&M Manager."].map((t) => (
                <p key={t} className="flex items-start gap-2 text-[13.5px] text-white">
                  <Check className="w-4 h-4 shrink-0 mt-0.5" style={{ color: GOLD }} /> {t}
                </p>
              ))}
            </div>
            <Link to="/om/assets" className="inline-flex items-center gap-1.5 text-[14px] font-semibold mt-7 hover:underline"
                  style={{ color: GOLD }} data-testid="om-landing-qr-link">
              See How QR Scanning Works <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="hidden lg:block"><PhoneMockup /></div>
        </div>
      </section>

      {/* 5 — Sector coverage */}
      <section className="max-w-[1280px] mx-auto px-5 sm:px-8 py-16 lg:py-20" data-testid="om-landing-sectors">
        <H2 testid="om-landing-sectors-heading">Purpose-Built for Every Indian Infrastructure Sector</H2>
        <Sub>
          Sector-specific equipment lists, KPI dashboards, compliance checklists, and RCA templates — pre-loaded
          for each industry.
        </Sub>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mt-10">
          {SECTORS.map((s) => (
            <Link key={s.slug} to={`/om/sectors/${s.slug}`}
                  className="group relative rounded-lg overflow-hidden border transition-colors duration-200 hover:border-[#C9A84C]"
                  style={{ background: CARD, borderColor: "rgba(255,255,255,0.1)" }}
                  data-testid={`om-landing-sector-${s.slug}`}>
              <img src={s.hero} alt={`${s.name} O&M`} loading="lazy"
                   className="w-full h-[112px] object-cover transition-all duration-300 group-hover:brightness-110"
                   style={{ filter: "brightness(0.62)" }} />
              <div className="absolute inset-x-0 bottom-0 p-3"
                   style={{ background: "linear-gradient(to top, rgba(10,22,40,0.94), transparent)" }}>
                <div className="text-[13px] font-bold text-white leading-tight">{s.name}</div>
                <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold mt-1" style={{ color: GOLD }}>
                  View Demo <ArrowRight className="w-3 h-3" />
                </span>
              </div>
            </Link>
          ))}
        </div>
        <p className="text-[13.5px] text-center mt-8" style={{ color: GOLD }}>
          Don't see your sector? Every O&amp;M module feature works for any asset type.{" "}
          <Link to="/contact" className="underline font-semibold" data-testid="om-landing-sectors-contact-link">Talk to us →</Link>
        </p>
      </section>

      {/* 6 — How it works */}
      <section className="border-y" style={{ background: STRIP, borderColor: "rgba(255,255,255,0.08)" }} data-testid="om-landing-steps">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 py-16 lg:py-20">
          <H2 testid="om-landing-steps-heading">Up and Running in Three Steps</H2>
          <Sub>
            No IT team required. No lengthy onboarding. Your O&amp;M team can be operational within one working day.
          </Sub>
          <div className="mt-10 relative">
            <span className="md:hidden absolute left-[19px] top-4 bottom-4 w-px" style={{ background: "rgba(201,168,76,0.35)" }} />
            <div className="grid md:grid-cols-[1fr_auto_1fr_auto_1fr] gap-5 md:gap-4 items-stretch">
              {STEPS.map(([title, text], i) => (
                <div key={title} className="contents">
                  <div className="relative rounded-lg border p-5 pl-14 md:pl-5"
                       style={{ background: CARD, borderColor: "rgba(255,255,255,0.1)" }} data-testid={`om-landing-step-${i + 1}`}>
                    <span className="absolute left-2.5 top-5 md:static flex items-center justify-center w-[34px] h-[34px] rounded-full font-display font-bold text-[15px] md:mb-3"
                          style={{ background: GOLD, color: NAVY }}>{i + 1}</span>
                    <h3 className="font-semibold text-[16px] text-white">{title}</h3>
                    <p className="text-[13px] mt-2 leading-relaxed" style={{ color: MUTED }}>{text}</p>
                  </div>
                  {i < STEPS.length - 1 ? (
                    <div className="hidden md:flex items-center justify-center">
                      <ArrowRight className="w-5 h-5" style={{ color: "rgba(201,168,76,0.6)" }} />
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
          <div className="text-center mt-10">
            <p className="text-[14px]" style={{ color: MUTED }}>
              Start your 14-day free trial and have your first asset registered today.
            </p>
            <Link to="/signup" className="inline-flex items-center h-11 px-6 rounded-lg text-[15px] font-semibold mt-4"
                  style={{ background: GOLD, color: NAVY }} data-testid="om-landing-steps-trial-btn">
              Start Free Trial <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* 7 — Comparison */}
      <section className="max-w-[1280px] mx-auto px-5 sm:px-8 py-16 lg:py-20" data-testid="om-landing-comparison">
        <H2 testid="om-landing-comparison-heading">Plan-Sarthi O&amp;M vs the Traditional Approach</H2>
        <div className="mt-10"><ComparisonTable /></div>
      </section>

      {/* 8 — Testimonials */}
      <section className="border-y" style={{ background: STRIP, borderColor: "rgba(255,255,255,0.08)" }} data-testid="om-landing-testimonials">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 py-16 lg:py-20">
          <H2 testid="om-landing-testimonials-heading">What O&amp;M Teams Say</H2>
          <div className="grid md:grid-cols-3 gap-4 mt-10">
            {VOICES.map(([quote, who], i) => (
              <div key={who} className="rounded-lg border p-6 flex flex-col" style={{ background: CARD, borderColor: "rgba(255,255,255,0.1)" }}
                   data-testid={`om-landing-testimonial-${i}`}>
                <span className="font-display font-bold leading-none" style={{ color: GOLD, fontSize: 44, opacity: 0.5 }} aria-hidden="true">“</span>
                <p className="text-[15px] text-white flex-1" style={{ lineHeight: 1.7 }}>{quote}</p>
                <div className="mt-5 pt-4 border-t text-[12.5px]" style={{ borderColor: "rgba(255,255,255,0.1)", color: MUTED }}>{who}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9 — Pricing reference */}
      <section className="max-w-[1280px] mx-auto px-5 sm:px-8 py-16 lg:py-20" data-testid="om-landing-pricing">
        <h2 className="font-display text-2xl sm:text-[28px] font-bold text-white text-center" data-testid="om-landing-pricing-heading">
          O&amp;M Module Is Included in Every Plan
        </h2>
        <Sub center>
          No separate licence. No add-on fee. Start with the 14-day free trial and access the full O&amp;M module
          from Day 1.
        </Sub>
        <div className="grid md:grid-cols-2 gap-4 mt-10">
          {PLANS.map(({ name, gold, blurb, items, cta: [label, to] }) => (
            <div key={name} className="rounded-lg border p-6 flex flex-col"
                 style={gold ? { background: GOLD, borderColor: GOLD } : { background: CARD, borderColor: GOLD }}
                 data-testid={`om-landing-plan-${name.split(" ")[0].toLowerCase()}`}>
              <h3 className="font-semibold text-[18px]" style={{ color: gold ? NAVY : "#FFFFFF" }}>{name}</h3>
              <p className="text-[13px] mt-1.5" style={{ color: gold ? "rgba(10,22,40,0.78)" : MUTED }}>{blurb}</p>
              <div className="space-y-2 mt-5 flex-1">
                {items.map((it) => (
                  <p key={it} className="flex items-start gap-2 text-[13.5px]" style={{ color: gold ? NAVY : "#FFFFFF" }}>
                    <Check className="w-4 h-4 shrink-0 mt-0.5" style={{ color: gold ? NAVY : GOLD }} /> {it}
                  </p>
                ))}
              </div>
              <Link to={to} className="inline-flex items-center justify-center h-11 px-6 rounded-lg text-[14.5px] font-semibold mt-6"
                    style={gold ? { background: NAVY, color: "#FFFFFF" } : { background: GOLD, color: NAVY }}
                    data-testid={`om-landing-plan-cta-${name.split(" ")[0].toLowerCase()}`}>
                {label} <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>
          ))}
        </div>
        <p className="text-[13px] text-center mt-6" style={{ color: MUTED }}>
          Exact pricing is shown on the{" "}
          <Link to="/pricing" className="underline" style={{ color: GOLD }} data-testid="om-landing-pricing-link">Pricing page →</Link>
        </p>
      </section>

      {/* 10 — Final CTA */}
      <section style={{ background: GOLD }} data-testid="om-landing-final-cta">
        <div className="max-w-[900px] mx-auto px-5 sm:px-8 py-14 text-center">
          <h2 className="font-display text-2xl sm:text-3xl font-bold" style={{ color: NAVY }}>
            Ready to Take Your O&amp;M Operations Digital?
          </h2>
          <p className="text-[15px] text-white mt-3 max-w-[640px] mx-auto">
            Join EPC contractors and O&amp;M operators across India who are using Plan-Sarthi to cut downtime,
            stay compliant, and manage assets smarter.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <Link to="/signup" className="inline-flex items-center h-12 px-8 rounded-lg text-[15px] font-bold text-white transition-transform duration-200 hover:-translate-y-0.5"
                  style={{ background: NAVY }} data-testid="om-landing-final-trial-btn">
              Start Free Trial <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
            <Link to="/contact" className="inline-flex items-center h-12 px-8 rounded-lg border-2 text-[15px] font-semibold transition-colors duration-200 hover:bg-[#0A1628]/10"
                  style={{ borderColor: NAVY, color: NAVY }} data-testid="om-landing-final-demo-btn">
              Book a 20-Minute Demo
            </Link>
          </div>
          <p className="text-[12.5px] mt-6" style={{ color: "rgba(10,22,40,0.7)" }}>
            14-day free trial. No credit card. Cancel anytime.
          </p>
        </div>
      </section>

      <Footer />
      <BackToTop />
    </div>
  );
}
